import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Activity, Moon, Flame, ChevronRight } from "lucide-react";

const WHOOP = "#00E0A4";

// Compact "readiness today" card for the home screen. Renders only when the user
// has WHOOP connected; otherwise nothing (Home falls back to the weekly snapshot).
export const WhoopReadiness = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data: status } = await api.get("/whoop/status");
        if (!active || !status.connected) { setReady(true); return; }
        const { data } = await api.get("/whoop/summary");
        if (active) setSummary(data);
      } catch (e) { /* ignore */ }
      finally { if (active) setReady(true); }
    })();
    return () => { active = false; };
  }, []);

  if (!ready || !summary) return null;

  const rec = summary.recovery?.recovery_score;
  const sleep = summary.sleep?.performance_pct;
  const strain = summary.strain?.day_strain;
  const recColor = rec == null ? "text-muted-foreground" : rec >= 67 ? "text-[#00E0A4]" : rec >= 34 ? "text-[#ccff0c]" : "text-destructive";

  const items = [
    { icon: Activity, label: "Recovery", value: rec == null ? "—" : `${rec}%`, cls: recColor },
    { icon: Moon, label: "Sleep", value: sleep == null ? "—" : `${sleep}%`, cls: "text-foreground" },
    { icon: Flame, label: "Strain", value: strain == null ? "—" : Math.round(strain * 10) / 10, cls: "text-foreground" },
  ];

  return (
    <Card data-testid="whoop-readiness-card" onClick={() => navigate("/progress")} className="rounded-2xl bg-card border-border p-4 cursor-pointer hover:border-[#00E0A4]/40 transition-colors">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full" style={{ background: WHOOP }} /> Readiness today
        </p>
        <ChevronRight className="h-4 w-4 text-muted-foreground" />
      </div>
      <div className="grid grid-cols-3 gap-3">
        {items.map((it) => (
          <div key={it.label} data-testid={`readiness-${it.label.toLowerCase()}`} className="text-center">
            <it.icon className="h-4 w-4 mx-auto mb-1" style={{ color: WHOOP }} />
            <p className={`font-mono text-2xl font-bold leading-none ${it.cls}`}>{it.value}</p>
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground mt-1">{it.label}</p>
          </div>
        ))}
      </div>
    </Card>
  );
};

export default WhoopReadiness;
